import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, ArrowLeft, ExternalLink, Lock } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatMinutes, formatNumber } from '../lib/format';
import {
  DIMENSIONS,
  codeText,
  evidenceLink,
  formatWorkDate,
  inLocale,
  type EvaluationDetail,
  type EvidenceItem,
} from '../lib/ai-points';
import { ApproveDialog, RunBadges, Score, StatusBadge } from './AiPoints';
import { Badge, Button, Card, CardHeader, Detail, ErrorState, LoadingBlock, PageHeader, Table, Td, Th, Tr } from '../components/ui';

/** One piece of evidence's facts, as short readable lines — the record's own words where it has them. */
function EvidenceFacts({ item }: { item: EvidenceItem }) {
  const { t, locale } = useI18n();
  const f = item.facts as Record<string, any>;
  const parts: string[] = [];
  const minutes = (v: unknown) => (typeof v === 'number' ? formatMinutes(v, locale) : '—');
  switch (item.kind) {
    case 'ATTENDANCE':
      parts.push(
        t('aiPoints.ev.attendance', {
          worked: minutes(f.workedMinutes),
          standard: f.standardMinutes != null ? minutes(f.standardMinutes) : t('aiPoints.notOwed'),
          surplus: minutes(f.surplusMinutes),
        }),
        t('aiPoints.ev.attendanceOff', { breaks: minutes(f.breakMinutes), paused: minutes(f.pausedMinutes), idle: minutes(f.notWorkingMinutes) }),
      );
      if (f.shift) parts.push(`${f.shift}${f.shiftWindow ? ` (${f.shiftWindow})` : ''}`);
      if (f.agentTracked === false) parts.push(t('aiPoints.flag.AGENT_UNTRACKED'));
      break;
    case 'FREE_SHIFT':
      parts.push(t('aiPoints.ev.free', { worked: minutes(f.workedMinutes), idle: minutes(f.notWorkingMinutes) }));
      break;
    case 'TASK':
      parts.push(codeText(t, 'aiPoints.verification', f.verification));
      if (f.priority) parts.push(t(`aiPoints.priority.${f.priority}` as TranslationKey));
      if (f.onTime === true) parts.push(t('aiPoints.ev.onTime'));
      if (f.onTime === false) parts.push(t('aiPoints.ev.late'));
      if (f.sentBack > 0) parts.push(t('aiPoints.ev.sentBack', { n: formatNumber(f.sentBack, locale) }));
      if (f.crossTeam) parts.push(t('aiPoints.ev.crossTeam'));
      if (f.selfCreated) parts.push(t('aiPoints.ev.selfCreated'));
      if (f.project) parts.push(String(f.project));
      break;
    case 'TICKET':
      if (f.priority) parts.push(t(`aiPoints.priority.${f.priority}` as TranslationKey));
      if (typeof f.minutesOpen === 'number') parts.push(t('aiPoints.ev.open', { time: minutes(f.minutesOpen) }));
      if (f.department) parts.push(String(f.department));
      break;
    case 'Q_TASK':
      if (f.priority) parts.push(t(`aiPoints.priority.${f.priority}` as TranslationKey));
      if (f.onTime === true) parts.push(t('aiPoints.ev.onTime'));
      if (f.onTime === false) parts.push(t('aiPoints.ev.late'));
      break;
    case 'CALL':
      parts.push(t('aiPoints.ev.call', { time: minutes(f.minutes), n: formatNumber(f.participants ?? 0, locale) }));
      if (f.summary) parts.push(t('aiPoints.ev.callSummary', { d: formatNumber(f.summary.decisions ?? 0, locale), a: formatNumber(f.summary.actionItems ?? 0, locale) }));
      if (f.meaningful === false) parts.push(t('aiPoints.ev.shortCall'));
      break;
    case 'REVIEW':
      parts.push(t(f.action === 'APPROVED' ? 'aiPoints.ev.reviewApproved' : 'aiPoints.ev.reviewSentBack'));
      break;
    case 'COMMENTS':
      parts.push(t('aiPoints.ev.comments', { t: formatNumber(f.tasks ?? 0, locale), k: formatNumber(f.tickets ?? 0, locale), q: formatNumber(f.qTasks ?? 0, locale) }));
      break;
    case 'BASELINE':
      parts.push(
        f.robust
          ? t('aiPoints.ev.baseline', { days: formatNumber(f.workedDays, locale), tasks: formatNumber(f.tasksPerDay, locale), ops: formatNumber(f.opsPerDay, locale) })
          : t('aiPoints.ev.baselineThin', { days: formatNumber(f.workedDays, locale) }),
      );
      if (f.team?.tasksPerDay != null) {
        parts.push(t('aiPoints.ev.team', { n: formatNumber(f.team.colleagues, locale), tasks: formatNumber(f.team.tasksPerDay, locale) }));
      }
      break;
  }
  return <span className="text-caption text-muted">{parts.filter(Boolean).join(' · ')}</span>;
}

/**
 * Route: `/ai-points/:id` — one employee's day in full, for administrators: every dimension's points
 * and plain-language reasons (never shown to the employee), the evidence with links back to the
 * records, the figures and baseline, the rubric and data snapshot it was made under, the model's
 * attempts, and the decision — proposed and actually credited kept apart.
 */
export function AiPointsDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const [approving, setApproving] = useState(false);
  const { data: ev, isLoading, error, refetch } = useQuery({
    queryKey: ['ai-points', 'detail', id],
    queryFn: () => api.get<EvaluationDetail>(`/ai-daily-points/evaluations/${id}`),
    refetchInterval: (q) => (q.state.data && ['QUEUED', 'EVALUATING'].includes(q.state.data.status) ? 5_000 : false),
  });

  const back = (
    <div className="mb-4">
      <Link to={ev ? `/ai-points?date=${ev.workDate}` : '/ai-points'} className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
        <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
        {t('aiPoints.title')}
      </Link>
    </div>
  );
  if (isLoading) return (<>{back}<LoadingBlock /></>);
  if (error || !ev) return (<>{back}<ErrorState error={error} onRetry={() => void refetch()} /></>);

  const byId = new Map(ev.evidence.map((e) => [e.id, e]));
  const m = ev.metrics ?? {};
  const noShift = m.dayKind === 'NO_SHIFT';
  const capOf = (key: string) => ev.rubricSnapshot?.caps.find((c) => c.key === key);

  return (
    <div className="max-w-6xl">
      {back}
      <PageHeader
        title={localized(ev.employee)}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            {formatWorkDate(ev.workDate, locale)}
            <RunBadges run={ev.run} />
            <StatusBadge status={ev.status} />
            {ev.statusReason && <span className="text-body-sm text-muted">{codeText(t, 'aiPoints.reason', ev.statusReason)}</span>}
          </span>
        }
        action={
          <>
            {ev.can.approve && (
              <Button variant="primary" onClick={() => setApproving(true)}>
                {t('aiPoints.approve')}
              </Button>
            )}
            {ev.can.reject && <Button onClick={() => navigate(`/ai-points/${ev.id}/reject`)}>{t('aiPoints.reject')}</Button>}
            {ev.can.retry && (
              <Button variant="ghost" onClick={() => navigate(`/ai-points/${ev.id}/retry`)}>
                {t(ev.status === 'PENDING_ADMIN_APPROVAL' ? 'aiPoints.regenerate' : 'aiPoints.retry')}
              </Button>
            )}
          </>
        }
      />

      <p className="mb-4 flex items-center gap-1.5 text-caption text-muted">
        <Lock className="h-3.5 w-3.5" aria-hidden />
        {t('aiPoints.adminOnly')}
      </p>
      {ev.selfNotice && (
        <Card className="mb-4">
          <p className="text-body-sm text-[var(--text-secondary)]">{t(`aiPoints.selfLong.${ev.selfNotice}` as TranslationKey)}</p>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* The proposal: points per dimension, the reasons in plain language, the evidence each rests on. */}
          <Card padded={false}>
            <div className="flex flex-wrap items-start justify-between gap-3 p-5 pb-3">
              <div className="min-w-0">
                <h3 className="text-h3 text-[var(--text-primary)]">{t('aiPoints.proposal')}</h3>
                {ev.confidence && (
                  <p className={clsx('mt-0.5 flex items-center gap-1 text-body-sm', ev.uncertainty === 'HIGH' ? 'text-warning-fg dark:text-warning-dark' : 'text-muted')}>
                    {ev.uncertainty === 'HIGH' && <AlertTriangle className="h-4 w-4" aria-hidden />}
                    {t('aiPoints.confidenceLine', {
                      confidence: t(`aiPoints.confidence.${ev.confidence}` as TranslationKey),
                      uncertainty: ev.uncertainty ? t(`aiPoints.uncertainty.${ev.uncertainty}` as TranslationKey) : '—',
                    })}
                  </p>
                )}
              </div>
              <Score row={ev} />
            </div>
            {ev.dimensions ? (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('aiPoints.dimension')}</Th>
                    <Th align="end">{t('aiPoints.points')}</Th>
                    <Th>{t('aiPoints.reasonCol')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {ev.dimensions.map((d) => (
                    <Tr key={d.key}>
                      <Td className="align-top">
                        <span className="block font-medium">{t(`aiPoints.dim.${d.key}` as TranslationKey)}</span>
                        <span className="text-caption text-muted">{codeText(t, 'aiPoints.capWhy', capOf(d.key)?.why)}</span>
                      </Td>
                      <Td align="end" className="whitespace-nowrap align-top tabular">
                        <span className={clsx('text-h3', d.points > 0 ? 'text-primary-600 dark:text-primary-400' : 'text-[var(--text-secondary)]')}>
                          {formatNumber(d.points, locale)}
                        </span>
                        <span className="block text-caption text-faint">{t('aiPoints.capOfMax', { cap: formatNumber(d.cap, locale), max: formatNumber(d.max, locale) })}</span>
                      </Td>
                      <Td className="align-top">
                        <p className="text-body-sm text-[var(--text-primary)]" dir="auto">
                          {inLocale(locale, d.reasonEn, d.reasonAr)}
                        </p>
                        {d.evidenceIds.length > 0 && (
                          <span className="mt-1.5 flex flex-wrap gap-1.5">
                            {d.evidenceIds.map((eid) => (
                              <a
                                key={eid}
                                href={`#ev-${eid}`}
                                className="max-w-full truncate rounded-xs bg-[var(--surface-sunken)] px-1.5 py-0.5 font-mono text-mono-sm text-primary-600 hover:underline dark:text-primary-400"
                              >
                                {eid}
                                {byId.get(eid)?.label ? ` — ${byId.get(eid)!.label!.slice(0, 40)}` : ''}
                              </a>
                            ))}
                          </span>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="px-5 pb-5 text-body-sm text-muted">{t('aiPoints.noProposal')}</p>
            )}
            {(ev.summaryEn || ev.summaryAr) && (
              <div className="border-t hairline p-5">
                <h4 className="mb-1 text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{t('aiPoints.summary')}</h4>
                <p className="text-body text-[var(--text-primary)]" dir="auto">
                  {inLocale(locale, ev.summaryEn, ev.summaryAr)}
                </p>
              </div>
            )}
            {ev.excluded && ev.excluded.length > 0 && (
              <div className="border-t hairline p-5">
                <h4 className="mb-1 text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{t('aiPoints.excluded')}</h4>
                <ul className="space-y-1 text-body-sm">
                  {ev.excluded.map((x, i) => (
                    <li key={i}>
                      {x.evidenceId && <span className="me-1.5 font-mono text-mono-sm text-faint">{x.evidenceId}</span>}
                      <span dir="auto">{inLocale(locale, x.whyEn, x.whyAr)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>

          {/* The evidence: what was read, as of the cutoff, each with its record when it is still there. */}
          <Card padded={false}>
            <div className="p-5 pb-3">
              <h3 className="text-h3 text-[var(--text-primary)]">{t('aiPoints.evidence')}</h3>
              <p className="text-body-sm text-muted">{t('aiPoints.evidenceHint', { at: formatDateTime(ev.dataCutoffAt, locale) })}</p>
            </div>
            {ev.evidence.length === 0 ? (
              <p className="px-5 pb-5 text-body-sm text-muted">{t('aiPoints.noEvidence')}</p>
            ) : (
              <ul className="divide-y divide-[var(--line)]">
                {ev.evidence.map((item) => {
                  const link = evidenceLink(item);
                  return (
                    <li key={item.id} id={`ev-${item.id}`} className="flex items-start gap-3 px-5 py-3">
                      <span className="mt-0.5 w-10 shrink-0 font-mono text-mono-sm text-faint">{item.id}</span>
                      <div className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <Badge tone="neutral">{t(`aiPoints.kind.${item.kind}` as TranslationKey)}</Badge>
                          {item.code && <span className="font-mono text-mono-sm text-[var(--text-secondary)]">{item.code}</span>}
                          {item.label && (
                            <span className="text-body-sm text-[var(--text-primary)]" dir="auto">
                              {item.label}
                            </span>
                          )}
                          {item.at && <span className="text-caption tabular text-muted">{formatDateTime(item.at, locale)}</span>}
                        </span>
                        <EvidenceFacts item={item} />
                      </div>
                      {link &&
                        (item.available === false ? (
                          <span className="shrink-0 text-caption text-muted">{t('aiPoints.sourceGone')}</span>
                        ) : (
                          <Link to={link} className="shrink-0 text-primary-600 hover:underline dark:text-primary-400" aria-label={t('aiPoints.openSource')}>
                            <ExternalLink className="h-4 w-4" aria-hidden />
                          </Link>
                        ))}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {/* Every look at the day: the model's calls, refused answers and why, retries and who asked. */}
          <Card padded={false}>
            <div className="p-5 pb-3">
              <h3 className="text-h3 text-[var(--text-primary)]">{t('aiPoints.attempts')}</h3>
              <p className="text-body-sm text-muted">{t('aiPoints.attemptsHint')}</p>
            </div>
            {ev.attemptHistory.length === 0 ? (
              <p className="px-5 pb-5 text-body-sm text-muted">{t('aiPoints.noAttempts')}</p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('aiPoints.when')}</Th>
                    <Th>{t('aiPoints.outcome')}</Th>
                    <Th>{t('aiPoints.detailsCol')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {ev.attemptHistory.map((a) => (
                    <Tr key={a.id}>
                      <Td className="whitespace-nowrap align-top">
                        <span className="block tabular">{formatDateTime(a.createdAt, locale)}</span>
                        <span className="text-caption text-muted">
                          {t('aiPoints.revisionAttempt', { r: formatNumber(a.revision, locale), a: formatNumber(a.attemptNo, locale) })} · {codeText(t, 'aiPoints.trigger', a.trigger)}
                        </span>
                      </Td>
                      <Td className="align-top">
                        <Badge tone={a.outcome === 'PROPOSED' ? 'success' : a.outcome === 'INVALID_OUTPUT' || a.outcome === 'PROVIDER_ERROR' ? 'error' : 'neutral'}>
                          {t(`aiPoints.outcome.${a.outcome}` as TranslationKey)}
                        </Badge>
                        {a.errorCode && <span className="mt-0.5 block font-mono text-mono-sm text-faint">{a.errorCode}</span>}
                      </Td>
                      <Td className="align-top text-body-sm">
                        {a.requestedBy && (
                          <span className="block">
                            {t('aiPoints.askedBy', { name: localized(a.requestedBy) })}
                            {a.reason && <span className="text-muted" dir="auto"> — {a.reason}</span>}
                          </span>
                        )}
                        {a.validationErrors && a.validationErrors.length > 0 && (
                          <ul className="list-inside list-disc text-caption text-error-fg dark:text-error-dark">
                            {a.validationErrors.slice(0, 6).map((v, i) => (
                              <li key={i}>{v}</li>
                            ))}
                          </ul>
                        )}
                        {(a.aiModel || a.inputTokens != null) && (
                          <span className="block text-caption text-muted">
                            {[a.aiModel, a.inputTokens != null ? t('aiPoints.tokens', { i: formatNumber(a.inputTokens, locale), o: formatNumber(a.outputTokens ?? 0, locale) }) : null, a.durationMs != null ? `${formatNumber(Math.round(a.durationMs / 100) / 10, locale)} s` : null]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          {/* The decision: proposed and actually credited, apart; who, when, the note. */}
          <Card>
            <CardHeader title={t('aiPoints.decision')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('aiPoints.proposed')}>{ev.score != null ? formatNumber(ev.score, locale) : '—'}</Detail>
              <Detail label={t('aiPoints.creditedLabel')}>
                {ev.status === 'APPROVED' ? formatNumber(ev.creditedPoints ?? 0, locale) : t('aiPoints.nothingCredited')}
              </Detail>
              <Detail label={t('aiPoints.reviewer')}>{ev.reviewedBy ? localized(ev.reviewedBy) : '—'}</Detail>
              <Detail label={t('aiPoints.decidedAt')}>{ev.reviewedAt ? formatDateTime(ev.reviewedAt, locale) : '—'}</Detail>
              {ev.walletTransactionId && (
                <Detail label={t('aiPoints.walletTx')}>
                  <span className="font-mono text-mono-sm">{ev.walletTransactionId}</span>
                </Detail>
              )}
              {ev.rejectNote && (
                <Detail label={t('aiPoints.rejectNote')}>
                  <span dir="auto">{ev.rejectNote}</span>
                </Detail>
              )}
            </dl>
          </Card>

          {/* The figures the caps were computed from. */}
          <Card>
            <CardHeader title={t('aiPoints.figures')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('aiPoints.fig.day')}>{codeText(t, 'aiPoints.dayKind', m.dayKind)}</Detail>
              <Detail label={t('aiPoints.fig.standard')}>
                {noShift ? '—' : m.regular?.standardMinutes != null ? formatMinutes(m.regular.standardMinutes, locale) : t('aiPoints.notOwed')}
              </Detail>
              <Detail label={t('aiPoints.fig.worked')}>{noShift || m.regular?.workedMinutes == null ? '—' : formatMinutes(m.regular.workedMinutes, locale)}</Detail>
              <Detail label={t('aiPoints.fig.surplus')}>{noShift ? '—' : formatMinutes(m.regular?.surplusMinutes ?? 0, locale)}</Detail>
              <Detail label={t('aiPoints.fig.free')}>
                {formatMinutes(m.free?.workedMinutes ?? 0, locale)} · {t('aiPoints.sessions', { n: formatNumber(m.free?.sessions ?? 0, locale) })}
              </Detail>
              <Detail label={t('aiPoints.fig.tasks')}>
                {t('aiPoints.fig.tasksValue', {
                  f: formatNumber(m.tasks?.finished ?? 0, locale),
                  v: formatNumber(m.tasks?.verified ?? 0, locale),
                  r: formatNumber(m.tasks?.awaitingReview ?? 0, locale),
                })}
              </Detail>
              <Detail label={t('aiPoints.fig.ops')}>{t('aiPoints.opsShort', { k: formatNumber(m.tickets ?? 0, locale), q: formatNumber(m.qTasks ?? 0, locale) })}</Detail>
              <Detail label={t('aiPoints.fig.calls')}>
                {t('aiPoints.fig.callsValue', { n: formatNumber(m.calls?.meaningful ?? 0, locale), time: formatMinutes(m.calls?.minutes ?? 0, locale) })}
              </Detail>
              <Detail label={t('aiPoints.fig.collab')}>
                {t('aiPoints.fig.collabValue', { r: formatNumber(m.collaboration?.reviews ?? 0, locale), c: formatNumber(m.collaboration?.comments?.total ?? 0, locale) })}
              </Detail>
              <Detail label={t('aiPoints.fig.baseline')}>
                {m.baseline?.robust
                  ? t('aiPoints.ev.baseline', { days: formatNumber(m.baseline.workedDays, locale), tasks: formatNumber(m.baseline.tasksPerDay, locale), ops: formatNumber(m.baseline.opsPerDay, locale) })
                  : t('aiPoints.ev.baselineThin', { days: formatNumber(m.baseline?.workedDays ?? 0, locale) })}
              </Detail>
            </dl>
            {ev.flags && ev.flags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {ev.flags.map((f) => (
                  <Badge key={f} tone="warning" title={codeText(t, 'aiPoints.flagHint', f)}>
                    {codeText(t, 'aiPoints.flag', f)}
                  </Badge>
                ))}
              </div>
            )}
          </Card>

          {/* What it was made under: the rubric, the thresholds, the model, the run. */}
          <Card>
            <CardHeader title={t('aiPoints.snapshot')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('aiPoints.rubricVersion')}>{ev.rubricVersion == null ? '—' : ev.rubricVersion === 0 ? t('aiPoints.defaultsVersion') : ev.rubricVersion}</Detail>
              {ev.rubricSnapshot &&
                DIMENSIONS.map((k) => (
                  <Detail key={k} label={t(`aiPoints.dim.${k}` as TranslationKey)}>
                    {t('aiPoints.capOfMax', { cap: formatNumber(capOf(k)?.cap ?? 0, locale), max: formatNumber(ev.rubricSnapshot!.rubric[k], locale) })}
                  </Detail>
                ))}
              <Detail label={t('aiPoints.model')}>{ev.aiModel ?? '—'}</Detail>
              <Detail label={t('aiPoints.promptVersion')}>{ev.promptVersion ?? '—'}</Detail>
              <Detail label={t('aiPoints.dataCutoff')}>{ev.dataCutoffAt ? formatDateTime(ev.dataCutoffAt, locale) : '—'}</Detail>
              <Detail label={t('aiPoints.generated')}>{ev.generatedAt ? formatDateTime(ev.generatedAt, locale) : '—'}</Detail>
              <Detail label={t('aiPoints.revision')}>{ev.revision}</Detail>
              <Detail label={t('aiPoints.timezone')}>{`${ev.run.timezone} · ${ev.run.dayStartsAt}`}</Detail>
              {ev.inputDigest && (
                <Detail label={t('aiPoints.digest')}>
                  <span className="font-mono text-mono-sm" title={ev.inputDigest}>
                    {ev.inputDigest.slice(0, 12)}…
                  </span>
                </Detail>
              )}
            </dl>
          </Card>
        </div>
      </div>
      {approving && <ApproveDialog row={ev} onClose={() => setApproving(false)} onDone={() => void refetch()} />}
    </div>
  );
}
